/**
 * AI Voice Service
 * Gerencia o reconhecimento de voz e a interpretação de comandos via Gemini AI.
 */
class AIVoiceService {
    constructor() {
        this.isListening = false;
        this.apiKey = null;
    }

    setApiKey(key) {
        this.apiKey = key;
    }

    /**
     * Inicia a escuta de voz. Cria uma nova instância a cada escuta para máxima compatibilidade.
     * @returns {Promise<string>} Transcrição do áudio.
     */
    listen() {
        return new Promise((resolve, reject) => {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SpeechRecognition) {
                return reject(new Error('Reconhecimento de voz não suportado neste navegador. Utilize o Google Chrome, Edge ou o aplicativo Android.'));
            }

            const recognition = new SpeechRecognition();
            recognition.lang = 'pt-BR';
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.maxAlternatives = 1;

            let resolved = false;

            recognition.onstart = () => {
                this.isListening = true;
                console.log('IA_VOICE: Microfone ativo, ouvindo...');
            };

            recognition.onresult = (event) => {
                this.isListening = false;
                if (event.results && event.results[0] && event.results[0][0]) {
                    const transcript = event.results[0][0].transcript;
                    console.log('IA_VOICE: Transcrição recebida:', transcript);
                    resolved = true;
                    resolve(transcript);
                } else {
                    reject(new Error('Não foi possível identificar o que você disse.'));
                }
            };

            recognition.onerror = (event) => {
                this.isListening = false;
                console.error('IA_VOICE: Erro no reconhecimento:', event.error);
                
                let errorMsg = 'Erro ao ouvir voz.';
                if (event.error === 'network') {
                    errorMsg = 'Erro de rede no Reconhecimento de Voz. Verifique sua conexão com a internet.';
                } else if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                    errorMsg = 'Permissão de microfone negada. Permita o microfone nas configurações do seu navegador ou celular.';
                } else if (event.error === 'no-speech') {
                    errorMsg = 'Nenhum som detectado. Fale mais perto do microfone.';
                } else if (event.error === 'audio-capture') {
                    errorMsg = 'Nenhum microfone detectado no seu dispositivo.';
                }
                
                reject(new Error(errorMsg));
            };

            recognition.onend = () => {
                this.isListening = false;
                console.log('IA_VOICE: Escuta finalizada.');
                if (!resolved) {
                    // Se finalizou sem resultado ou erro prévio
                    setTimeout(() => {
                        if (!resolved) {
                            reject(new Error('Nenhum áudio foi capturado. Tente novamente.'));
                        }
                    }, 300);
                }
            };

            try {
                recognition.start();
            } catch (err) {
                this.isListening = false;
                reject(new Error('Não foi possível iniciar o microfone: ' + err.message));
            }
        });
    }

    /**
     * Interpreta o comando de voz usando o Gemini.
     * @param {string} transcript Texto transcrito.
     * @returns {Promise<Object>} Dados da transação extraídos.
     */
    async interpretCommand(transcript) {
        if (!this.apiKey) {
            console.error('IA_VOICE: Chave da API não encontrada');
            throw new Error('Chave da API Gemini não configurada.');
        }

        if (!transcript || transcript.trim().length === 0) {
            throw new Error('Não foi possível ouvir o que você disse.');
        }

        // Import dinâmico para code-splitting: SDK só é carregado se o usuário usar comando de voz
        const { GoogleGenerativeAI, SchemaType } = await import("@google/generative-ai");

        const responseSchema = {
            type: SchemaType.OBJECT,
            properties: {
                amount: { type: SchemaType.NUMBER, description: "Valor monetário total gasto ou recebido" },
                type: { type: SchemaType.STRING, enum: ["income", "expense"], description: "income para ganhos e expense para gastos" },
                category: { type: SchemaType.STRING, enum: ["Uber", "99", "iFood", "Rappi", "Zé Delivery", "Lalamove", "Loggi", "inDrive", "Particular", "Gorjeta", "Gasolina", "Etanol", "GNV", "Elétrico", "Alimentação", "Manutenção", "Lava Jato", "Contas Fixas", "Outros"], description: "A categoria que melhor se encaixa" },
                description: { type: SchemaType.STRING, description: "Nome do local, posto, ou breve resumo (ex: Posto Ipiranga, Almoço)" },
                discountValue: { type: SchemaType.NUMBER, description: "Apenas se houver desconto falado explicitamente" },
                discountType: { type: SchemaType.STRING, enum: ["R$", "%"], description: "Tipo de desconto se houver" },
                fuelQuantity: { type: SchemaType.NUMBER, description: "Quantidade abastecida em litros, m3 ou kWh" },
                fuelPrice: { type: SchemaType.NUMBER, description: "Preço unitário do litro/m3/kWh do combustível" },
                paymentMethod: { type: SchemaType.STRING, enum: ["Cartão de Crédito", "Cartão de Débito", "Dinheiro", "Pix", "Vale/App Combustível", "Outro"], description: "Forma como foi pago o gasto" },
                tripCount: { type: SchemaType.NUMBER, description: "Quantidade de viagens ou corridas feitas (apenas para ganhos)" }
            },
            required: ["amount", "type", "category"]
        };

        const tryInterpret = async (modelName) => {
            const genAI = new GoogleGenerativeAI(this.apiKey);
            const model = genAI.getGenerativeModel({ 
                model: modelName,
                systemInstruction: "Você é um assistente financeiro inteligente para motoristas de aplicativo e entregadores. Extraia os dados da transcrição rigorosamente no formato JSON. Seja determinístico. Se uma informação opcional (como desconto, litros, viagens, etc) não foi dita, omita-a do JSON. Mapeie plataformas para Uber, 99, iFood, Rappi, Zé Delivery, Lalamove, Loggi, inDrive ou Particular. Mapeie combustíveis para Gasolina, Etanol, GNV ou Elétrico.",
                generationConfig: {
                    temperature: 0.0,
                    responseMimeType: "application/json",
                    responseSchema: responseSchema
                }
            });

            const prompt = `Transcrição: "${transcript}"`;
            const result = await model.generateContent(prompt);
            const response = await result.response;
            return response.text().trim();
        };

        try {
            let text;
            try {
                console.log('IA_VOICE: Interpretando com gemini-2.5-flash...');
                text = await tryInterpret("gemini-2.5-flash");
            } catch (e) {
                console.warn('IA_VOICE: gemini-2.5-flash falhou, tentando gemini-flash-latest...', e);
                text = await tryInterpret("gemini-flash-latest");
            }
            
            console.log('IA_VOICE: Resposta da IA:', text);
            const parsed = JSON.parse(text);

            if (!parsed.amount || parsed.amount <= 0) {
                throw new Error('Não identifiquei um valor no comando. Tente dizer: "Gastei 50 reais de gasolina".');
            }

            return parsed;
        } catch (error) {
            console.error('IA_VOICE: Erro completo:', error);
            throw new Error(error.message || 'Não foi possível interpretar o comando de voz.');
        }
    }
}

const aiVoiceService = new AIVoiceService();
export default aiVoiceService;
